<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { PageHero, ReportProblemLink } from '~/components';
import {
  AppBreadcrumbs,
  AppSkeletonLoader,
  AppTypography,
} from '~/components/base';
import { CampaignDetailModal } from '~/components/campaigns';
import {
  CampaignMiniCard,
  LobbyistRow,
  SourcesDialog,
} from '~/components/candidates';
import { CandidatesModal, PartyProfileCard } from '~/components/parties';
import { useParty } from '~/composables/useParty';
import type { Chamber } from '~/types/Candidates';
import { $sanitizeHtml } from '~/utils/helpers';
import { normalizeExternalUrl } from '~/utils/normalizeExternalUrl';

definePageMeta({ layout: 'default' });

// Figma: three rows of three cards per page in the 3-across grid (same
// Campaigns section design as the candidate profile).
const CAMPAIGNS_PAGE_SIZE = 9;
const CANDIDATES_PAGE_SIZE = 5;

type TabValue = 'about' | 'candidates' | 'campaigns' | 'lobbyists';
const TABS: { value: TabValue; label: string }[] = [
  { value: 'about', label: 'About' },
  { value: 'candidates', label: 'Candidates' },
  { value: 'campaigns', label: 'Campaigns' },
  { value: 'lobbyists', label: 'Lobbyists' },
];

const route = useRoute();

// Captured once: pages remount per fullPath (see default layout page-key),
// so the id can't legitimately change while this component lives. Reading
// it reactively would flash the not-found state during back-navigation
// (the route empties params just before unmount).
const partyId = route.params.id as string;

const {
  party,
  candidates,
  campaigns,
  lobbyists,
  pending: partyPending,
} = useParty(partyId);

// Head/meta AFTER the data refs they read — placing them above caused a
// TDZ ReferenceError during client-side navigation.
useHead({ title: computed(() => party.value?.name ?? 'Party Profile') });
useSeoMeta({
  description: () => party.value?.description ?? 'Party profile on VoteWise.',
  ogTitle: () => `${party.value?.name ?? 'Party Profile'} | VoteWise`,
  ogDescription: () => party.value?.description ?? 'Party profile on VoteWise.',
});

const activeTab = ref<TabValue>(
  route.query.tab === 'candidates' ||
    route.query.tab === 'campaigns' ||
    route.query.tab === 'lobbyists'
    ? (route.query.tab as TabValue)
    : 'about',
);

const breadcrumbs = [
  { label: 'Parties', to: '/parties' },
  { label: 'Party Profile' },
];

const websiteHref = computed(() => normalizeExternalUrl(party.value?.website));

const hasWebsite = computed(() => Boolean(party.value?.website));

// Bio is stored as HTML on the backend; sanitize before rendering with
// v-html (same DOMPurify helper used by the candidate profile page).
const sanitizedBio = computed(() => $sanitizeHtml(party.value?.description));

// Per-chamber copy for when THAT chamber has no candidates but the other
// does (Figma: "Party Profile - No District/Region Candidates" frames) —
// distinct from the fully-empty state below, which covers neither chamber
// having any candidates at all.
const CHAMBER_GROUPS: {
  value: Chamber;
  label: string;
  emptyTitle: string;
  emptyDescription: string;
}[] = [
  {
    value: 'lower',
    label: 'District Candidates',
    emptyTitle: 'No district representatives',
    emptyDescription:
      "This party doesn't have any local representatives to display.",
  },
  {
    value: 'upper',
    label: 'Region Candidates',
    emptyTitle: 'No regional representatives',
    emptyDescription:
      "This party doesn't have any regional representatives to display.",
  },
];

const candidatesModalVisible = ref(false);
const selectedChamber = ref<Chamber | null>(null);

const candidateGroups = computed(() =>
  CHAMBER_GROUPS.map((group) => {
    const groupCandidates = candidates.value.filter(
      (c) => c.chamber === group.value,
    );
    return {
      ...group,
      candidates: groupCandidates,
      visibleCandidates: groupCandidates.slice(0, CANDIDATES_PAGE_SIZE),
      hasMore: groupCandidates.length > CANDIDATES_PAGE_SIZE,
    };
  }),
);

const hasAnyCandidates = computed(() => candidates.value.length > 0);

const openCandidatesModal = (chamber: Chamber) => {
  selectedChamber.value = chamber;
  candidatesModalVisible.value = true;
};

const visibleCampaignsCount = ref(CAMPAIGNS_PAGE_SIZE);
const visibleCampaigns = computed(() =>
  campaigns.value.slice(0, visibleCampaignsCount.value),
);
const hasMoreCampaigns = computed(
  () => visibleCampaignsCount.value < campaigns.value.length,
);

function showMoreCampaigns() {
  visibleCampaignsCount.value += CAMPAIGNS_PAGE_SIZE;
}

const sourcesVisible = ref(false);
const hasSources = computed(() => Boolean(party.value?.sources?.length));

// Desktop previews a campaign in a modal instead of navigating (the mini card
// emits `open` only at that breakpoint). Holding the id rather than a boolean
// lets the modal mount fresh per campaign, so its one-shot fetch runs for the
// one that was clicked.
const openCampaignId = ref<string | null>(null);
</script>

<template>
  <div class="party-page">
    <PageHero constrained max-width="max-w-6xl">
      <!-- Mobile shows a back arrow in the top nav instead (Figma). The
           breadcrumb's arrow pops real history, so arriving from a search
           result returns to those results instead of the Parties list —
           matching the candidate and campaign profiles. -->
      <AppBreadcrumbs
        :custom-breadcrumbs="breadcrumbs"
        :include-home="false"
        class="hidden lg:flex"
      />

      <template v-if="party">
        <div
          class="party-page__layout flex flex-col items-start gap-6 lg:flex-row"
        >
          <PartyProfileCard
            :party="party"
            :campaigns-count="campaigns.length"
            :lobbyists-count="lobbyists.length"
          />

          <!-- min-w-0: a flex item defaults to min-width:auto, so wide panel
               content (a long unbreakable string, a table) would otherwise
               refuse to narrow and squeeze the profile card instead. -->
          <div class="party-page__panel w-full min-w-0 flex-1">
            <nav class="party-page__tabs flex w-full items-stretch">
              <button
                v-for="tab in TABS"
                :key="tab.value"
                type="button"
                :data-testid="`party-tab-${tab.value}`"
                class="party-page__tab flex-1"
                :class="{ 'party-page__tab--active': activeTab === tab.value }"
                @click="activeTab = tab.value"
              >
                {{ tab.label }}
              </button>
            </nav>

            <!-- About -->
            <div
              v-if="activeTab === 'about'"
              data-testid="party-panel-about"
              class="party-page__section flex flex-col gap-5"
            >
              <div>
                <AppTypography type="body-bold" element="p">Bio</AppTypography>
                <!-- eslint-disable vue/no-v-html -->
                <div
                  class="party-page__bio font-body text-color-secondary mt-1"
                  v-html="sanitizedBio"
                ></div>
                <!-- eslint-enable vue/no-v-html -->
              </div>

              <div
                v-if="hasWebsite || hasSources"
                class="party-page__divider"
              />

              <div v-if="hasWebsite">
                <div class="flex items-center gap-1.5">
                  <AppTypography type="body-bold" element="p"
                    >Website</AppTypography
                  >
                  <Icon name="solar:square-top-down-linear" class="shrink-0" />
                </div>
                <a
                  :href="websiteHref"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="party-page__website-link mt-1 inline-block"
                >
                  <AppTypography type="body" class="text-color-brand">
                    {{ party.website }}
                  </AppTypography>
                </a>
              </div>

              <button
                v-if="hasSources"
                type="button"
                data-testid="sources-button"
                class="party-page__sources flex items-center justify-between"
                @click="sourcesVisible = true"
              >
                <span class="flex items-center gap-2">
                  <Icon name="solar:link-round-linear" class="shrink-0" />
                  <AppTypography type="caption-bold" element="span"
                    >Sources</AppTypography
                  >
                </span>
                <Icon name="solar:alt-arrow-right-linear" class="shrink-0" />
              </button>

              <ReportProblemLink
                report-type="parties"
                :report-id="party?.id"
                class="mt-4"
              />
            </div>

            <!-- Candidates -->
            <div
              v-else-if="activeTab === 'candidates'"
              data-testid="party-panel-candidates"
              class="party-page__section flex flex-col gap-6"
            >
              <div class="mt-2 mb-4">
                <AppTypography type="body-bold" element="p" class="mb-2">
                  Associated Candidates
                </AppTypography>
                <AppTypography type="subheadline" element="p">
                  Candidates associated with this political party.
                </AppTypography>
              </div>

              <template v-if="hasAnyCandidates">
                <div
                  v-for="group in candidateGroups"
                  :key="group.value"
                  class="flex flex-col gap-3"
                >
                  <div class="flex items-center justify-between gap-6">
                    <AppTypography
                      type="body-bold"
                      element="p"
                      class="text-color-primary"
                    >
                      {{ group.label }}
                    </AppTypography>
                    <button
                      v-if="group.hasMore"
                      type="button"
                      :data-testid="`view-all-${group.value}`"
                      class="party-page__view-all flex items-center gap-1"
                      @click="openCandidatesModal(group.value)"
                    >
                      <AppTypography
                        type="footnote-bold"
                        element="span"
                        class="underline"
                      >
                        View All
                      </AppTypography>
                      <Icon
                        name="solar:alt-arrow-right-linear"
                        class="shrink-0"
                        size="14"
                      />
                    </button>
                  </div>

                  <div
                    v-if="group.visibleCandidates.length"
                    class="party-page__cand-list flex flex-col gap-3"
                  >
                    <NuxtLink
                      v-for="candidate in group.visibleCandidates"
                      :key="candidate.id"
                      :to="`/candidates/${candidate.id}`"
                      data-testid="party-candidate-row"
                      class="party-page__cand-row flex items-center gap-4 no-underline"
                    >
                      <span
                        class="party-page__cand-avatar flex shrink-0 items-center justify-center"
                      >
                        <img
                          v-if="candidate.image"
                          :src="candidate.image"
                          :alt="candidate.name"
                          class="party-page__cand-avatar-image size-full object-cover"
                        />
                        <AppTypography v-else type="body-bold" element="span">
                          {{ candidate.initials }}
                        </AppTypography>
                      </span>

                      <div class="flex min-w-0 flex-1 items-center gap-2">
                        <AppTypography
                          type="body-bold"
                          element="p"
                          class="truncate"
                        >
                          {{ candidate.name }}
                        </AppTypography>
                      </div>

                      <span
                        v-if="candidate.incumbent"
                        class="party-page__cand-chip inline-flex w-fit shrink-0 items-center"
                      >
                        <AppTypography
                          type="caption-bold"
                          element="span"
                          class="uppercase"
                        >
                          Incumbent
                        </AppTypography>
                      </span>

                      <Icon
                        name="solar:alt-arrow-right-linear"
                        class="party-page__cand-chevron shrink-0"
                      />
                    </NuxtLink>
                  </div>

                  <div
                    v-else
                    :data-testid="`candidates-empty-state-${group.value}`"
                    class="party-page__empty flex flex-col items-center gap-3 text-center"
                  >
                    <img
                      src="/no-associated-items.svg"
                      width="195"
                      height="166"
                      alt=""
                      aria-hidden="true"
                      class="party-page__empty-illustration"
                    />
                    <AppTypography type="title-6">{{
                      group.emptyTitle
                    }}</AppTypography>
                    <AppTypography type="body" class="text-color-tertiary">
                      {{ group.emptyDescription }}
                    </AppTypography>
                  </div>
                </div>
              </template>

              <div
                v-else
                data-testid="candidates-empty-state"
                class="party-page__empty flex flex-col items-center gap-3 text-center"
              >
                <img
                  src="/no-associated-items.svg"
                  width="195"
                  height="166"
                  alt=""
                  aria-hidden="true"
                  class="party-page__empty-illustration"
                />
                <AppTypography type="title-6"
                  >No candidates available</AppTypography
                >
                <AppTypography
                  type="body"
                  class="text-color-tertiary lg:hidden"
                >
                  This party doesn't have any candidates listed yet.
                </AppTypography>
                <AppTypography
                  type="body"
                  class="text-color-tertiary hidden lg:block"
                >
                  There are no candidates to display at the moment.
                </AppTypography>
              </div>
            </div>

            <!-- Campaigns -->
            <div
              v-else-if="activeTab === 'campaigns'"
              data-testid="party-panel-campaigns"
              class="party-page__section flex flex-col gap-4"
            >
              <div class="mt-2 mb-4">
                <AppTypography type="body-bold" element="p" class="mb-2">
                  Associated Campaigns
                </AppTypography>
                <AppTypography type="subheadline" element="p">
                  Campaigns associated with this political party.
                </AppTypography>
              </div>

              <template v-if="visibleCampaigns.length">
                <div class="grid grid-cols-2 gap-2 lg:grid-cols-3 lg:gap-4">
                  <CampaignMiniCard
                    v-for="campaign in visibleCampaigns"
                    :key="campaign.id"
                    :campaign="campaign"
                    @open="openCampaignId = campaign.id"
                  />
                </div>

                <div v-if="hasMoreCampaigns" class="flex justify-center">
                  <button
                    type="button"
                    data-testid="show-more-campaigns"
                    class="party-page__show-more"
                    @click="showMoreCampaigns"
                  >
                    <AppTypography type="subheadline-bold" element="span">
                      Show More
                    </AppTypography>
                  </button>
                </div>
              </template>

              <div
                v-else
                data-testid="campaigns-empty-state"
                class="party-page__empty flex flex-col items-center gap-3 text-center"
              >
                <img
                  src="/no-associated-items.svg"
                  width="195"
                  height="166"
                  alt=""
                  aria-hidden="true"
                  class="party-page__empty-illustration"
                />
                <AppTypography type="title-6">No Campaigns Yet</AppTypography>
                <AppTypography type="body" class="text-color-tertiary">
                  There are no campaigns to display at the moment.
                </AppTypography>
              </div>
            </div>

            <!-- Lobbyists -->
            <div
              v-else
              data-testid="party-panel-lobbyists"
              class="party-page__section flex flex-col gap-4"
            >
              <div class="mt-2 mb-4">
                <AppTypography type="body-bold" element="p" class="mb-2">
                  Supporting Lobbyists
                </AppTypography>
                <AppTypography type="subheadline" element="p">
                  Lobbying organizations and individuals associated to this
                  political party.
                </AppTypography>
              </div>

              <template v-if="lobbyists.length">
                <div class="flex flex-col gap-3">
                  <LobbyistRow
                    v-for="lobbyist in lobbyists"
                    :key="lobbyist.id"
                    :lobbyist="lobbyist"
                  />
                </div>

                <div class="party-page__note">
                  <AppTypography
                    type="caption-bold"
                    element="p"
                    class="uppercase"
                  >
                    Note:
                  </AppTypography>
                  <AppTypography type="footnote" element="p" class="mt-1">
                    Affiliation data is drawn from public disclosure registers
                    only. Inclusion does not imply wrongdoing or any formal
                    relationship with this party.
                  </AppTypography>
                </div>

                <button
                  v-if="hasSources"
                  type="button"
                  data-testid="sources-button-lobbyists"
                  class="party-page__sources party-page__sources--lobbyists flex items-center justify-between"
                  @click="sourcesVisible = true"
                >
                  <span class="flex items-center gap-2">
                    <Icon name="solar:link-round-bold" class="shrink-0" />
                    <AppTypography type="caption-bold" element="span"
                      >Sources</AppTypography
                    >
                  </span>
                  <Icon name="solar:alt-arrow-right-linear" class="shrink-0" />
                </button>
              </template>

              <div
                v-else
                data-testid="lobbyists-empty-state"
                class="party-page__empty flex flex-col items-center gap-3 text-center"
              >
                <img
                  src="/no-associated-items.svg"
                  width="195"
                  height="166"
                  alt=""
                  aria-hidden="true"
                  class="party-page__empty-illustration"
                />
                <AppTypography type="title-6">No Lobbyists Yet</AppTypography>
                <AppTypography type="body" class="text-color-tertiary">
                  There are no lobbyists to display at the moment.
                </AppTypography>
              </div>
            </div>
          </div>
        </div>
      </template>

      <div
        v-else-if="partyPending"
        data-testid="party-loading"
        class="party-page__layout flex flex-col items-start gap-6 lg:flex-row"
      >
        <AppSkeletonLoader
          :items="[{ height: '420px' }]"
          container-class="w-full lg:w-[360px] lg:shrink-0"
        />
        <AppSkeletonLoader
          :items="[{ height: '480px' }]"
          container-class="w-full min-w-0 flex-1"
        />
      </div>
      <div
        v-else
        class="party-page__not-found flex flex-col items-center gap-3 text-center"
        data-testid="party-not-found"
      >
        <AppTypography type="title-6">Party not found</AppTypography>
        <AppTypography type="body" class="text-color-tertiary">
          We couldn't find the party you're looking for.
        </AppTypography>
      </div>
    </PageHero>

    <SourcesDialog
      v-model:visible="sourcesVisible"
      :sources="party?.sources ?? []"
    />
    <CampaignDetailModal
      v-if="openCampaignId"
      :key="openCampaignId"
      :campaign-id="openCampaignId"
      :visible="true"
      @update:visible="openCampaignId = null"
    />
    <CandidatesModal
      v-if="selectedChamber"
      v-model:visible="candidatesModalVisible"
      :candidates="candidates.filter((c) => c.chamber === selectedChamber)"
      :chamber-label="
        CHAMBER_GROUPS.find((g) => g.value === selectedChamber)?.label || ''
      "
    />
  </div>
</template>

<style scoped lang="scss">
.party-page {
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  min-height: calc(100vh - var(--header-height));

  &__panel {
    // Mobile (Figma): the tabs + content sit directly on the sand band with
    // no card wrapper. The white rounded card is a desktop-only treatment.
    @media (min-width: 1024px) {
      padding: $spacing-2xl;
      border-radius: $radius-2xl;
      background-color: $surface-color-bg;
    }
  }

  &__tabs {
    // Figma: complementary/darker-shade-01 (#766035) — no matching design
    // token, so set the literal value.
    border-bottom: 1px solid #766035;
  }

  &__tab {
    @include font-subheadline;
    padding: $spacing-sm;
    border: none;
    border-bottom: 4px solid transparent;
    background: transparent;
    color: #766035;
    font-weight: 600;
    text-align: center;
    cursor: pointer;

    &--active {
      border-bottom-color: $text-color-brand;
      color: $text-color-brand;
    }
  }

  &__section {
    padding-top: $spacing-xl;
  }

  &__bio {
    // The sanitized bio may contain block tags (<p>, <ul>...). Keep paragraph
    // spacing readable; the browser's default <p> margins would otherwise be
    // reset away by the global reset.
    :deep(p) {
      margin: 0;
    }

    :deep(p + p) {
      margin-top: $spacing-md;
    }
  }

  &__divider {
    height: 1px;
    // Figma: borders/default (#e8e3db) — this app's `$border-color` token is
    // a different (navy) palette used elsewhere, so set the literal value.
    background-color: #e8e3db;

    @media (max-width: 639.98px) {
      background-color: $complementary-color-shade-1;
    }
  }

  // Figma: text/primary (#201e1a) — matches the candidate profile's
  // `__subtext` treatment for the same supporting copy under a section
  // heading (e.g. "Campaigns associated with this political party.").
  &__subtext {
    color: #201e1a;
  }

  // Figma: "Show More" is a plain underlined text link centred under the
  // grid, matching the candidate profile's Campaigns tab — not an outlined
  // pill button.
  &__show-more {
    border: none;
    background: transparent;
    color: $text-color-primary;
    cursor: pointer;
    text-decoration: underline;
  }

  &__sources {
    width: 100%;
    // No design value to hand — capped so the chevron doesn't drift to the
    // far edge of the panel, matching the candidate profile's About-tab
    // Sources button.
    max-width: 400px;
    padding: $spacing-md $spacing-lg;
    border: 1px solid $border-color-tag;
    border-radius: $radius-sm;
    background: transparent;
    color: $text-color-secondary;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: $text-color-brand;
      color: $text-color-brand;
    }

    &--lobbyists {
      max-width: 100%;
      height: 48px;

      @media (min-width: 1024px) {
        max-width: 320px;
      }
    }
  }

  &__note {
    padding: $spacing-lg;
    border-radius: $radius-md;
    background-color: $border-color-tag;
    color: white;
  }

  &__empty {
    padding: $spacing-6xl $spacing-lg;

    &-illustration {
      width: 100%;
      max-width: 195px;
      height: auto;
    }
  }

  &__not-found {
    padding: $spacing-6xl $spacing-lg;
  }

  &__view-all {
    border: none;
    background: transparent;
    color: $text-color-brand;
    cursor: pointer;
  }

  &__cand-list {
    // Figma: rows sit inside one card on a shared background, separated by
    // hairline dividers, instead of each being its own individually
    // bordered box — at every breakpoint, not just mobile. No horizontal
    // padding on the card itself — its rows must align flush with the
    // District/Region Candidates label above, so the row's own padding
    // (below) is what insets the content.
    gap: 0;
    border-radius: $radius-lg;
    background-color: $surface-color-bg;
    overflow: hidden;
  }

  &__cand-row {
    padding: $spacing-md 0;
    border: none;
    border-radius: 0;
    border-bottom: 1px solid $complementary-color-shade-2;
    color: $text-color-primary;
    transition: all 0.15s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: $surface-color-bg-2;
    }

    &.router-link-active {
      background-color: transparent;
    }
  }

  &__cand-chevron {
    color: $text-color-subtext;
  }

  &__cand-avatar {
    width: 44px;
    height: 44px;
    // Clips the photo to the circle; without it the square image corners
    // spill past the rounded border.
    overflow: hidden;
    border: 2px solid $complementary-color;
    border-radius: 9999px;
    background-color: $surface-color-bg;
    color: $text-color-primary;
    font-weight: 700;
    font-size: 0.875rem;

    &-image {
      border-radius: 9999px;
    }
  }

  &__cand-chip {
    padding: 2px $spacing-sm;
    border-radius: $radius-2xl;
    background-color: $complementary-color-shade-2;
    color: $text-color-subtext;
    font-weight: 600;
    font-size: 0.75rem;
  }
}
</style>
